import React, { useState } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import QRCode from "qrcode.react";
import { Button, Icon, Input, Message, Segment } from "semantic-ui-react";
import api from "../../../api";
import { updateUsuario } from "../../../actions/auth";
import SenhaInput from "../../layouts/SenhaInput";
import * as FormValidator from "../../../utils/formValidator";
import { removerTodosDispositivos2fa } from "../../../utils/dispositivo2fa";

const TwoFactorConfig = ({ onConcluido }) => {
  const dispatch = useDispatch();
  const ativo = useSelector(state => !!(state.user.user && state.user.user.twoFactor && state.user.user.twoFactor.enabled));

  const [etapa, setEtapa] = useState("inicio"); // inicio | configurar | codigos | desativar
  const [setup, setSetup] = useState(null);
  const [codigo, setCodigo] = useState("");
  const [senha, setSenha] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [erro, setErro] = useState("");
  const [loading, setLoading] = useState(false);
  const [encerrado, setEncerrado] = useState(false);

  const concluir = () => {
    reset();
    if (onConcluido) onConcluido();
  };

  const reset = () => {
    setEtapa("inicio");
    setSetup(null);
    setCodigo("");
    setSenha("");
    setErro("");
  };

  const executar = async fn => {
    setLoading(true);
    setErro("");
    try {
      await fn();
    } catch (e) {
      setErro(FormValidator.getErro(e));
    }
    setLoading(false);
  };

  const iniciar = () => executar(async () => {
    setSetup(await api.usuario.twoFactorSetup());
    setEtapa("configurar");
  });

  const ativar = () => executar(async () => {
    const res = await api.usuario.twoFactorEnable(codigo.trim());
    setRecoveryCodes(res.recoveryCodes);
    setCodigo("");
    setEtapa("codigos");
    await dispatch(updateUsuario());
  });

  const encerrarDispositivos = () => executar(async () => {
    await api.usuario.encerrarDispositivos2fa();
    removerTodosDispositivos2fa();
    setEncerrado(true);
  });

  const desativar = () => executar(async () => {
    await api.usuario.twoFactorDisable(senha, codigo.trim());
    await dispatch(updateUsuario());
    reset();
  });

  const email = useSelector(state => (state.user.user && state.user.user.email) || "");

  const opcao = (selecionada, icone, titulo, texto, acao) => (
    <div
      style={{
        flex: "1 1 240px",
        border: `2px solid ${selecionada ? "#e83c6e" : "#e4e4e8"}`,
        borderRadius: 10,
        padding: 16,
        background: selecionada ? "rgba(232,60,110,.06)" : "transparent"
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 6 }}>
        <Icon name={icone} /> {titulo}
        {selecionada && <span style={{ marginLeft: 8, color: "#1fa855", fontSize: 12 }}><Icon name="check circle" />Em uso</span>}
      </div>
      <p style={{ fontSize: 13, margin: "0 0 10px" }}>{texto}</p>
      {acao}
    </div>
  );

  return (
    <Segment style={{ marginTop: 30 }}>
      <h3><Icon name="shield alternate" /> Verificação em duas etapas</h3>
      <p>
        A verificação é <b>obrigatória</b> em todo login: além da senha, é pedido um código.
        Escolha como quer recebê-lo.
      </p>

      {erro && <Message negative size="small">{erro}</Message>}

      {etapa === "inicio" && (
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
          {opcao(
            !ativo, "mail outline", "Código por e-mail",
            `Enviamos um código de 6 dígitos para ${email || "o seu e-mail"} a cada login.`,
            ativo && (
              <Button type="button" size="tiny" basic onClick={() => setEtapa("desativar")}>
                Voltar a receber por e-mail
              </Button>
            )
          )}
          {opcao(
            ativo, "mobile alternate", "Aplicativo autenticador",
            "Google Authenticator, Authy, 1Password... O código muda a cada 30 segundos e não depende do e-mail.",
            !ativo && (
              <Button type="button" className="btn-ui-1" primary size="tiny" loading={loading} onClick={iniciar}>
                <Icon name="lock" /> Usar aplicativo
              </Button>
            )
          )}
        </div>
      )}

      {etapa === "inicio" && (
        <p style={{ fontSize: 13, marginTop: 14 }}>
          Ao entrar, você pode marcar <b>Lembrar deste navegador por 7 dias</b> para não digitar o código nesse período.{" "}
          {encerrado
            ? <span style={{ color: "#1fa855" }}><Icon name="check circle" />Navegadores lembrados encerrados.</span>
            : <button type="button" className="va-cad-link" disabled={loading} onClick={encerrarDispositivos}>Encerrar navegadores lembrados</button>}
        </p>
      )}

      {etapa === "configurar" && setup && (
        <div>
          <p><b>1.</b> Escaneie o QR code no aplicativo autenticador:</p>
          <div style={{ margin: "10px 0" }}><QRCode value={setup.otpauthUrl} size={160} /></div>
          <p style={{ fontSize: 12 }}>
            Ou digite a chave manualmente: <code style={{ wordBreak: "break-all" }}>{setup.secret}</code>
          </p>
          <p><b>2.</b> Digite o código de 6 dígitos gerado:</p>
          <Input
            placeholder="000000"
            inputMode="numeric"
            maxLength={6}
            value={codigo}
            onChange={e => setCodigo(e.target.value)}
            style={{ marginRight: 10 }}
          />
          <Button type="button" className="btn-ui-1" primary size="tiny" loading={loading} disabled={codigo.length !== 6} onClick={ativar}>
            Confirmar e ativar
          </Button>
          <Button type="button" size="tiny" basic onClick={reset}>Cancelar</Button>
        </div>
      )}

      {etapa === "codigos" && (
        <div>
          <Message warning size="small">
            Guarde estes códigos de recuperação em local seguro. Cada um pode ser usado uma vez
            caso você perca acesso ao aplicativo. Eles não serão exibidos novamente.
          </Message>
          <pre style={{ background: "#f4f4f4", padding: 12, columnCount: 2 }}>{recoveryCodes.join("\n")}</pre>
          <Button type="button" className="btn-ui-1" primary size="tiny" onClick={concluir}>Já guardei os códigos</Button>
        </div>
      )}

      {etapa === "desativar" && (
        <div>
          <p>Para voltar a receber o código por e-mail, confirme sua senha e informe um código atual do aplicativo (ou de recuperação).</p>
          <div style={{ maxWidth: 320, marginBottom: 10 }}>
            <SenhaInput placeholder="Senha" value={senha} onChange={e => setSenha(e.target.value)} />
          </div>
          <Input placeholder="Código" maxLength={10} value={codigo} onChange={e => setCodigo(e.target.value)} style={{ marginRight: 10 }} />
          <Button type="button" negative size="tiny" loading={loading} disabled={!senha || !codigo} onClick={desativar}>
            Usar e-mail
          </Button>
          <Button type="button" size="tiny" basic onClick={reset}>Cancelar</Button>
        </div>
      )}
    </Segment>
  );
};

TwoFactorConfig.propTypes = {
  onConcluido: PropTypes.func
};

export default TwoFactorConfig;
